import { Typography } from '@mui/material';
import { CreateLLMModelSpecificationRequest, LLMModelSpecification } from 'api/model';
import { getSchema, getUiSchema } from 'api/model-specifications/formSchema';
import useModelSpecification from 'api/model-specifications/useModelSpecification';
import Loader from 'components/Loader';
import FormBlock from 'components/form/FormBlock';
import FormButtons from 'components/form/FormButtons';
import { SettingsFormShell } from 'components/settingsHub/SettingsFormShell';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import SpecificationSourceLink from './SpecificationSourceLink';
import useModelSpecifications from 'api/model-specifications/useModelSpecifications';

const FORM_ID = 'model-spec-edit-form';

type Props = {
  onClose: () => void;
  modelSpecification: LLMModelSpecification;
};

const ModelSpecificationEdit = ({ onClose, modelSpecification }: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [provider, setProvider] = useState(modelSpecification.provider);

  const {
    modelSpecification: latestModelSpecification,
    isLoading,
    update,
  } = useModelSpecification({
    name: modelSpecification.name,
    provider: modelSpecification.provider,
    modelSpecification,
  });

  const { modelSpecifications } = useModelSpecifications(provider);

  const providerGroups = useMemo(
    () => Array.from(new Set(modelSpecifications?.map((spec) => spec.group).filter((group) => group))).sort(),
    [modelSpecifications],
  );

  const specificationMissing = !isLoading && !latestModelSpecification;
  const closed = useRef(false);

  useEffect(() => {
    if (!specificationMissing || closed.current) return;
    closed.current = true;

    toast.error('Model specification does not exist');
    onClose();
  }, [specificationMissing, onClose]);

  if (isLoading) return <Loader />;

  if (!latestModelSpecification) return null;

  const handleSubmit = async (formData: CreateLLMModelSpecificationRequest) => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      await update.mutateAsync(formData);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (formData: CreateLLMModelSpecificationRequest) => {
    if (formData.provider && formData.provider !== provider) {
      setProvider(formData.provider);
    }
  };

  return (
    <SettingsFormShell
      title="Edit Model Specification"
      footer={<FormButtons formId={FORM_ID} onCancel={onClose} cancelBtnTxt="back to list" isLoading={isSaving} />}
    >
      <Typography variant="body2" color="textSecondary" className="mb-4">
        Modify the LLM model specification details.
        <br />
        Specification is taken from the <SpecificationSourceLink />.
      </Typography>
      <FormBlock
        isLoading={isSaving}
        formId={FORM_ID}
        showButtons={false}
        initialValues={latestModelSpecification}
        schema={getSchema()}
        uiSchema={getUiSchema(providerGroups)}
        onSubmit={(formData) => void handleSubmit(formData)}
        onChange={handleChange}
        onCancel={onClose}
      ></FormBlock>
    </SettingsFormShell>
  );
};

export default ModelSpecificationEdit;
